import { SimpleOrgMember } from '@euler/model/SimpleOrgMember';
import {
  BiDashboardGrantInput,
  BiDashboardVisibility,
} from '@euler/model/bi/dashboard';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useState } from 'react';
import { biSameGrants } from './biAudience';

export type BiSharingState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      grants: BiDashboardGrantInput[];
      /** The store's members: who can be granted, and their names. */
      members: SimpleOrgMember[];
    }
  | { kind: 'failed' };

/** A dashboard's grants and the store's members, read when the panel opens. */
export function useBiSharing(dashboardId: string, visible: boolean) {
  const { biService, storeService } = useServiceFactory();
  const [state, setState] = useState<BiSharingState>({ kind: 'loading' });
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!visible) return;
    let active = true;
    setState({ kind: 'loading' });
    Promise.all([
      biService.dashboardGrants(dashboardId),
      storeService.getSimpleMemberList(),
    ])
      .then(([grants, members]) => {
        if (!active) return;
        setState({
          kind: 'ready',
          grants: grants.map(({ memberId, permission }) => ({
            memberId,
            permission,
          })),
          members,
        });
      })
      .catch(error => {
        console.error('BI sharing failed', dashboardId, error);
        if (active) setState({ kind: 'failed' });
      });
    return () => {
      active = false;
    };
  }, [biService, storeService, dashboardId, visible, reload]);

  const retry = useCallback(() => setReload(value => value + 1), []);

  /** Writes what changed: the visibility, then the grants. */
  const save = useCallback(
    async (
      before: {
        visibility: BiDashboardVisibility;
        grants: BiDashboardGrantInput[];
      },
      after: {
        visibility: BiDashboardVisibility;
        grants: BiDashboardGrantInput[];
      },
    ) => {
      if (after.visibility !== before.visibility)
        await biService.setDashboardVisibility(dashboardId, after.visibility);
      if (!biSameGrants(before.grants, after.grants))
        await biService.replaceDashboardGrants(dashboardId, {
          grants: after.grants,
        });
    },
    [biService, dashboardId],
  );

  return { state, retry, save };
}
